import { AuthErrorCodes } from 'firebase/auth';

/**
 * Firebase Auth error codes as strongly-typed constants
 */
export const FIREBASE_ERROR_CODES = {
  // Authentication
  INVALID_PASSWORD: AuthErrorCodes.INVALID_PASSWORD,
  WRONG_PASSWORD: 'auth/wrong-password',
  INVALID_CREDENTIAL: 'auth/invalid-credential',
  INVALID_LOGIN_CREDENTIALS: 'auth/invalid-login-credentials',

  // User Management
  USER_DELETED: AuthErrorCodes.USER_DELETED,
  USER_NOT_FOUND: 'auth/user-not-found',
  USER_DISABLED: 'auth/user-disabled',

  // Session & Reauthentication
  TOKEN_EXPIRED: AuthErrorCodes.TOKEN_EXPIRED,
  USER_TOKEN_EXPIRED: 'auth/user-token-expired',
  REQUIRES_RECENT_LOGIN: 'auth/requires-recent-login',
  CREDENTIAL_TOO_OLD: 'auth/credential-too-old-login-again',
  UNSUPPORTED_FIRST_FACTOR: 'auth/unsupported-first-factor',

  // Email & Account
  INVALID_EMAIL: 'auth/invalid-email',
  EMAIL_ALREADY_IN_USE: 'auth/email-already-in-use',

  // Password & Validation
  WEAK_PASSWORD: 'auth/weak-password',
  PASSWORD_REQUIREMENTS: 'auth/password-does-not-meet-requirements',
  INVALID_VERIFICATION_CODE: 'auth/invalid-verification-code',
  INVALID_VERIFICATION_ID: 'auth/invalid-verification-id',
  CODE_EXPIRED: 'auth/code-expired',

  // Multi-Factor Authentication
  MFA_REQUIRED: 'auth/multi-factor-auth-required',

  // Operations
  OPERATION_NOT_ALLOWED: 'auth/operation-not-allowed',
  OPERATION_NOT_SUPPORTED: 'auth/operation-not-supported-in-this-environment',

  // Custom Token
  INVALID_CUSTOM_TOKEN: 'auth/invalid-custom-token',

  // Rate Limiting
  TOO_MANY_REQUESTS: AuthErrorCodes.TOO_MANY_ATTEMPTS_TRY_LATER,
  TOO_MANY_ATTEMPTS: 'auth/too-many-requests',

  // Network
  NETWORK_REQUEST_FAILED: 'auth/network-request-failed',
  TIMEOUT: 'auth/timeout',

  // System
  INTERNAL_ERROR: 'auth/internal-error',
} as const;

export type FirebaseErrorCode = (typeof FIREBASE_ERROR_CODES)[keyof typeof FIREBASE_ERROR_CODES];

/**
 * Error code groups for logical operations
 */
export const ERROR_CODE_GROUPS = {
  CREDENTIAL_ERRORS: [
    FIREBASE_ERROR_CODES.INVALID_PASSWORD,
    FIREBASE_ERROR_CODES.WRONG_PASSWORD,
    FIREBASE_ERROR_CODES.INVALID_CREDENTIAL,
    FIREBASE_ERROR_CODES.INVALID_LOGIN_CREDENTIALS,
  ],

  USER_NOT_FOUND_ERRORS: [FIREBASE_ERROR_CODES.USER_DELETED, FIREBASE_ERROR_CODES.USER_NOT_FOUND],

  REAUTH_ERRORS: [
    FIREBASE_ERROR_CODES.USER_TOKEN_EXPIRED,
    FIREBASE_ERROR_CODES.REQUIRES_RECENT_LOGIN,
    FIREBASE_ERROR_CODES.CREDENTIAL_TOO_OLD,
    FIREBASE_ERROR_CODES.UNSUPPORTED_FIRST_FACTOR,
  ],

  NETWORK_ERRORS: [FIREBASE_ERROR_CODES.NETWORK_REQUEST_FAILED, FIREBASE_ERROR_CODES.TIMEOUT],

  RATE_LIMIT_ERRORS: [
    FIREBASE_ERROR_CODES.TOO_MANY_REQUESTS,
    FIREBASE_ERROR_CODES.TOO_MANY_ATTEMPTS,
  ],

  PASSWORD_REQUIREMENT_ERRORS: [
    FIREBASE_ERROR_CODES.WEAK_PASSWORD,
    FIREBASE_ERROR_CODES.PASSWORD_REQUIREMENTS,
  ],
} as const;

export interface FirebaseErrorMapping {
  message: string;
  severity: 'error' | 'warning' | 'info';
  category: 'auth' | 'network' | 'validation' | 'system';
  userAction?: string;
}

export interface FirebaseAuthError extends Error {
  code: string;
  customData?: { message?: string };
}

// Base error objects for reuse (only when all fields are identical)
const baseInvalidCredentialsError: FirebaseErrorMapping = {
  message: 'Incorrect email or password',
  severity: 'error',
  category: 'auth',
  userAction: 'Check your email and password and try again.',
};

const baseReauthError: FirebaseErrorMapping = {
  message: 'For security reasons, please confirm your password to continue.',
  severity: 'warning',
  category: 'auth',
  userAction: 'Enter your password to continue.',
};

const basePasswordRequirementsError: FirebaseErrorMapping = {
  message:
    'Password must be at least 8 characters and contain upper/lower case letters and numbers.',
  severity: 'error',
  category: 'validation',
  userAction: 'Create a stronger password with at least 8 characters.',
};

const baseUserNotFoundError: FirebaseErrorMapping = {
  message:
    "We couldn't find an account with that email address or password. Please double-check and try again.",
  severity: 'error',
  category: 'auth',
  userAction: 'Check your email and password or create a new account.',
};

const FIREBASE_ERROR_MAPPINGS = new Map<string, FirebaseErrorMapping>([
  // Authentication errors
  [FIREBASE_ERROR_CODES.INVALID_PASSWORD, { ...baseInvalidCredentialsError }],
  [FIREBASE_ERROR_CODES.WRONG_PASSWORD, { ...baseInvalidCredentialsError }],
  [FIREBASE_ERROR_CODES.INVALID_CREDENTIAL, { ...baseInvalidCredentialsError }],
  [FIREBASE_ERROR_CODES.INVALID_LOGIN_CREDENTIALS, { ...baseInvalidCredentialsError }],
  [FIREBASE_ERROR_CODES.USER_DELETED, { ...baseUserNotFoundError }],
  [FIREBASE_ERROR_CODES.USER_NOT_FOUND, { ...baseUserNotFoundError }],
  [
    FIREBASE_ERROR_CODES.USER_DISABLED,
    {
      message: 'This account has been disabled. Please contact support for assistance.',
      severity: 'error',
      category: 'auth',
      userAction: 'Contact support to reactivate your account.',
    },
  ],
  [
    FIREBASE_ERROR_CODES.INVALID_EMAIL,
    {
      message: 'Please enter a valid email address.',
      severity: 'error',
      category: 'validation',
      userAction: 'Enter a valid email address.',
    },
  ],
  [
    FIREBASE_ERROR_CODES.EMAIL_ALREADY_IN_USE,
    {
      message: 'An account with this email address already exists. Please sign in instead.',
      severity: 'error',
      category: 'auth',
      userAction: 'Try signing in or use a different email address.',
    },
  ],

  // Rate limiting and security
  [
    FIREBASE_ERROR_CODES.TOO_MANY_REQUESTS,
    {
      message: 'Too many login attempts. Please try again later or reset your password.',
      severity: 'error',
      category: 'auth',
      userAction: 'Wait a few minutes before trying again or reset your password.',
    },
  ],
  [
    FIREBASE_ERROR_CODES.TOO_MANY_ATTEMPTS,
    {
      message: 'Too many failed attempts. Please try again later or reset your password.',
      severity: 'error',
      category: 'auth',
      userAction: 'Wait a few minutes before trying again or reset your password.',
    },
  ],

  // Token and session errors
  [
    FIREBASE_ERROR_CODES.TOKEN_EXPIRED,
    {
      message: 'Your session has expired. Please sign in again.',
      severity: 'warning',
      category: 'auth',
      userAction: 'Sign in again to continue.',
    },
  ],
  [
    FIREBASE_ERROR_CODES.USER_TOKEN_EXPIRED,
    {
      ...baseReauthError,
      message: 'Your session has expired. Please confirm your password to continue.',
    },
  ],
  [FIREBASE_ERROR_CODES.REQUIRES_RECENT_LOGIN, { ...baseReauthError }],
  [FIREBASE_ERROR_CODES.CREDENTIAL_TOO_OLD, { ...baseReauthError }],

  // Network errors
  [
    FIREBASE_ERROR_CODES.NETWORK_REQUEST_FAILED,
    {
      message: 'Network error. Please check your internet connection and try again.',
      severity: 'error',
      category: 'network',
      userAction: 'Check your internet connection and try again.',
    },
  ],
  [
    FIREBASE_ERROR_CODES.TIMEOUT,
    {
      message: 'Request timed out. Please try again.',
      severity: 'error',
      category: 'network',
      userAction: 'Try again in a moment.',
    },
  ],

  // Password requirements
  [FIREBASE_ERROR_CODES.WEAK_PASSWORD, { ...basePasswordRequirementsError }],
  [FIREBASE_ERROR_CODES.PASSWORD_REQUIREMENTS, { ...basePasswordRequirementsError }],
  [
    FIREBASE_ERROR_CODES.INTERNAL_ERROR,
    {
      message: 'Something went wrong. Please try again.',
      severity: 'error',
      category: 'system',
      userAction: 'Try again in a moment.',
    },
  ],

  // Multi-factor authentication
  [
    FIREBASE_ERROR_CODES.MFA_REQUIRED,
    {
      message: 'Multi-factor authentication required.',
      severity: 'info',
      category: 'auth',
      userAction: 'Complete multi-factor authentication to continue.',
    },
  ],
  [
    FIREBASE_ERROR_CODES.INVALID_VERIFICATION_CODE,
    {
      message: 'The verification code you entered is invalid. Please try again.',
      severity: 'error',
      category: 'validation',
      userAction: 'Enter the correct verification code.',
    },
  ],
  [
    FIREBASE_ERROR_CODES.CODE_EXPIRED,
    {
      message: 'The verification code has expired. Please request a new one.',
      severity: 'warning',
      category: 'validation',
      userAction: 'Request a new verification code.',
    },
  ],
  [
    FIREBASE_ERROR_CODES.INVALID_VERIFICATION_ID,
    {
      message: 'Invalid verification. Please try again.',
      severity: 'error',
      category: 'validation',
      userAction: 'Start the verification process again.',
    },
  ],

  // Operation errors
  [
    FIREBASE_ERROR_CODES.OPERATION_NOT_ALLOWED,
    {
      message: 'This sign-in method is not enabled. Please contact support.',
      severity: 'error',
      category: 'system',
      userAction: 'Contact support for assistance.',
    },
  ],
  [
    FIREBASE_ERROR_CODES.OPERATION_NOT_SUPPORTED,
    {
      message: 'This operation is not supported in your current environment.',
      severity: 'error',
      category: 'system',
      userAction: 'Try using a different browser or device.',
    },
  ],

  // Custom token errors
  [
    FIREBASE_ERROR_CODES.INVALID_CUSTOM_TOKEN,
    {
      message: 'The custom token format is incorrect. Please verify your token.',
      severity: 'error',
      category: 'validation',
      userAction: 'Check your authentication token and try again.',
    },
  ],
]);

// Default fallback error mapping
const DEFAULT_ERROR_MAPPING: FirebaseErrorMapping = {
  message: 'An unexpected error occurred. Please try again.',
  severity: 'error',
  category: 'system',
  userAction: 'Try again in a moment.',
};

/**
 * Enhanced error handler for Firebase Auth errors
 * @description Provides consistent error messages across web and mobile platforms.
 * Handles Firebase and firebase/auth customData fallback for accurate error mapping.
 *
 * @example
 * ```typescript
 * // Basic error message handling
 * const message = FirebaseErrorHandler.getErrorMessage(firebaseError);
 * console.log(message); // "Incorrect email or password"
 *
 * // Error type checking
 * if (FirebaseErrorHandler.isCredentialError(errorCode)) {
 *   // Handle credential errors
 * }
 *
 * if (FirebaseErrorHandler.requiresMFA(errorCode)) {
 *   // Handle MFA requirement
 * }
 *
 * if (FirebaseErrorHandler.requiresReauth(errorCode)) {
 *   // Prompt user for reauthentication
 * }
 *
 * // Error categorization
 * const categories = FirebaseErrorHandler.getCategorizedErrorCodes();
 * console.log(categories.auth); // All auth-related error codes
 *
 * // Error severity analysis
 * const severities = FirebaseErrorHandler.getErrorCodesBySeverity();
 * console.log(severities.error); // All error-level codes
 * ```
 */
export class FirebaseErrorHandler {
  /**
   * Get error mapping for a Firebase error code
   * @param errorCode - Firebase error code
   * @returns Error mapping with message, severity, category, and user action
   */
  static getErrorMapping(errorCode: string): FirebaseErrorMapping {
    return FIREBASE_ERROR_MAPPINGS.get(errorCode) || DEFAULT_ERROR_MAPPING;
  }

  /**
   * Get user-friendly error message for a Firebase error
   * @param error - Firebase error object or error code string
   * @returns Localized error message
   * @description Uses intelligent fallback to extract real error code from customData when needed
   */
  static getErrorMessage(error: FirebaseAuthError | string): string {
    let errorCode: string;

    if (typeof error === 'string') {
      errorCode = error;
    } else {
      errorCode = error.code;

      // Check customData for more specific error codes (Firebase SDK v11+ behavior)
      if (error.customData?.message) {
        const extractedCode = this.extractErrorCodeFromMessage(error.customData.message);

        if (extractedCode && extractedCode !== errorCode && this.hasMapping(extractedCode)) {
          errorCode = extractedCode;
        }
      }
    }

    return this.getErrorMapping(errorCode).message;
  }

  /**
   * Extract Firebase error code from customData message
   * @param message - Custom data message containing error details
   * @returns Extracted error code or null if not found
   * @private
   */
  private static extractErrorCodeFromMessage(message: string): string | null {
    if (!message) {
      return null;
    }

    const match = message.match(/auth\/[\w-]+/);
    return match ? match[0] : null;
  }

  /**
   * Check if error requires reauthentication
   * @param errorCode - Firebase error code to check
   * @returns True if error requires user reauthentication
   */
  static requiresReauth(errorCode: string): boolean {
    return (ERROR_CODE_GROUPS.REAUTH_ERRORS as readonly string[]).includes(errorCode);
  }

  /**
   * Check if error is related to wrong password/credentials
   * @param errorCode - Firebase error code to check
   * @returns True if error is credential-related
   */
  static isCredentialError(errorCode: string): boolean {
    return (ERROR_CODE_GROUPS.CREDENTIAL_ERRORS as readonly string[]).includes(errorCode);
  }

  /**
   * Check if error is network-related
   * @param errorCode - Firebase error code to check
   * @returns True if error is network-related
   */
  static isNetworkError(errorCode: string): boolean {
    return (ERROR_CODE_GROUPS.NETWORK_ERRORS as readonly string[]).includes(errorCode);
  }

  /**
   * Check if error requires rate limiting display
   * @param errorCode - Firebase error code to check
   * @returns True if error is due to too many attempts
   */
  static isTooManyAttempts(errorCode: string): boolean {
    return (ERROR_CODE_GROUPS.RATE_LIMIT_ERRORS as readonly string[]).includes(errorCode);
  }

  /**
   * Check if error requires multi-factor authentication
   * @param errorCode - Firebase error code to check
   * @returns True if MFA is required
   */
  static requiresMFA(errorCode: string): boolean {
    return errorCode === FIREBASE_ERROR_CODES.MFA_REQUIRED;
  }

  /**
   * Check if error is user not found related
   * @param errorCode - Firebase error code to check
   * @returns True if error is user not found related
   */
  static isUserNotFoundError(errorCode: string): boolean {
    return (ERROR_CODE_GROUPS.USER_NOT_FOUND_ERRORS as readonly string[]).includes(errorCode);
  }

  /**
   * Check if error is password requirement related
   * @param errorCode - Firebase error code to check
   * @returns True if error is password requirement related
   */
  static isPasswordRequirementError(errorCode: string): boolean {
    return (ERROR_CODE_GROUPS.PASSWORD_REQUIREMENT_ERRORS as readonly string[]).includes(errorCode);
  }

  /**
   * Get all mapped error codes
   * @returns Array of all error codes with mappings
   * @description Useful for testing and validation purposes
   */
  static getAllMappedCodes(): string[] {
    return Array.from(FIREBASE_ERROR_MAPPINGS.keys());
  }

  /**
   * Get categorized error codes
   * @returns Object with error codes grouped by category
   * @description Useful for documentation and debugging
   */
  static getCategorizedErrorCodes(): Record<string, string[]> {
    const categories: Record<string, string[]> = {};

    FIREBASE_ERROR_MAPPINGS.forEach((mapping, code) => {
      if (!categories[mapping.category]) {
        categories[mapping.category] = [];
      }
      categories[mapping.category].push(code);
    });

    return categories;
  }

  /**
   * Get error codes by severity
   * @returns Object with error codes grouped by severity level
   */
  static getErrorCodesBySeverity(): Record<string, string[]> {
    const severities: Record<string, string[]> = {};

    FIREBASE_ERROR_MAPPINGS.forEach((mapping, code) => {
      if (!severities[mapping.severity]) {
        severities[mapping.severity] = [];
      }
      severities[mapping.severity].push(code);
    });

    return severities;
  }

  /**
   * Validate if an error code has a mapping
   * @param errorCode - Firebase error code to validate
   * @returns True if error code has a mapping
   */
  static hasMapping(errorCode: string): boolean {
    return FIREBASE_ERROR_MAPPINGS.has(errorCode);
  }

  /**
   * Enhanced error processing with custom message support
   * @param error - Firebase error object with optional customData
   * @returns Processed Firebase auth error
   * @description Maintains backward compatibility with existing localizeError function
   */
  static processError(
    error: FirebaseAuthError & { customData?: { message?: string } },
  ): FirebaseAuthError {
    let errorMapping = FIREBASE_ERROR_MAPPINGS.get(error.code);

    // Legacy support: check customData for error codes
    if (!errorMapping && error.customData?.message) {
      const customMessage = error.customData.message;

      for (const [code, mapping] of FIREBASE_ERROR_MAPPINGS.entries()) {
        if (customMessage.includes(code)) {
          errorMapping = mapping;
          break;
        }
      }
    }

    if (errorMapping) {
      const processedError = new Error(errorMapping.message) as FirebaseAuthError;
      processedError.code = error.code;
      processedError.name = 'FirebaseAuthError';
      return processedError;
    }

    return error;
  }
}

/**
 * @deprecated Use FirebaseErrorHandler.getErrorMessage() instead
 * @param error - Firebase error object with code property
 * @returns Localized error message
 */
export function getFirebaseErrorMessage(error: FirebaseAuthError | { code: string }): string {
  return FirebaseErrorHandler.getErrorMessage(error.code);
}

/**
 * @deprecated Use FirebaseErrorHandler.processError() instead
 * @param error - Firebase auth error object
 * @returns Processed Firebase auth error
 */
export function localizeError(error: FirebaseAuthError): FirebaseAuthError {
  return FirebaseErrorHandler.processError(error);
}

export { FIREBASE_ERROR_MAPPINGS, DEFAULT_ERROR_MAPPING };

// Validation: Ensure all constants in FIREBASE_ERROR_CODES have mappings
const validateErrorMappings = () => {
  const missingMappings: string[] = [];

  Object.values(FIREBASE_ERROR_CODES).forEach((errorCode) => {
    if (!FIREBASE_ERROR_MAPPINGS.has(errorCode)) {
      missingMappings.push(errorCode);
    }
  });

  if (missingMappings.length > 0) {
    console.warn('Firebase Error Handler: Missing mappings for error codes:', missingMappings);
  }

  return missingMappings.length === 0;
};

// Run validation in development
if (process.env.NODE_ENV === 'development') {
  validateErrorMappings();
}
